/**
 * The six fields F33 requires before a CheckUp score can be generated.
 *
 * Snake_case because these are the server's canonical field KEYS, and every
 * client uses them to look up an i18n label and to ask for the right input.
 * A union rather than `string`, so a renamed key is a compile error in every
 * client at once.
 */
export type ProfileRequiredField =
  'name' | 'date_of_birth' | 'nationality' | 'current_country' | 'current_city' | 'phone';

// ─── Profile strength ─────────────────────────────────────────────────────
//
// The whole profile, item by item, and what each item is FOR. Decided on the
// server (`profile-strength.rules.ts`) from the same stored fields the CheckUp
// gate and the iQulture calculation read; every client renders it verbatim.

/** The five sections of Settings → Profile, in render order. */
export const PROFILE_STRENGTH_SECTIONS = [
  'identity',
  'mobility',
  'skills',
  'education',
  'experience',
] as const;
export type ProfileStrengthSectionId = (typeof PROFILE_STRENGTH_SECTIONS)[number];

/** Every item the strength counts — equal weight, one each. */
export type ProfileStrengthItemKey =
  | 'name'
  | 'date_of_birth'
  | 'phone'
  | 'residence'
  | 'nationality'
  | 'passport'
  | 'work_model'
  | 'current_role'
  | 'years_experience'
  | 'skills'
  | 'languages'
  | 'education'
  | 'experience';

/**
 * What an item is for:
 *  • `start`   — the CheckUp cannot start without it (the F33 gate);
 *  • `score`   — the iQulture calculation reads it;
 *  • `journey` — used outside the score (TelePortal, companies).
 */
export type ProfileStrengthTag = 'start' | 'score' | 'journey';

/** The word beside the number. Thresholds live on the server with the rule. */
export type ProfileStrengthLevel = 'getting_started' | 'good' | 'strong' | 'complete';

/**
 * Where a complete item's value was found. Education and experience also
 * count from the Career CV, because the score falls back to it; the profile
 * section then has nothing of its own to show, and says why.
 */
export type ProfileStrengthItemSource = 'profile' | 'career_cv';

export interface ProfileStrengthItem {
  key: ProfileStrengthItemKey;
  complete: boolean;
  /** Null while the item is missing. */
  source: ProfileStrengthItemSource | null;
  tags: ProfileStrengthTag[];
  /** An identity document (passport / ID) can fill it. */
  fillableFromDocument: boolean;
}

export interface ProfileStrengthSection {
  id: ProfileStrengthSectionId;
  completeItems: number;
  totalItems: number;
  items: ProfileStrengthItem[];
}

export interface ProfileReadinessMeter {
  completeItems: number;
  totalItems: number;
  /**
   * What is missing, in render order. The `score` meter leaves out what the
   * `start` meter already lists, so no item is asked for twice.
   */
  missing: ProfileStrengthItemKey[];
}

/** The most useful thing to do next — one upload, or one item. */
export type ProfileNextStep =
  | { kind: 'document'; fills: ProfileStrengthItemKey[] }
  | { kind: 'item'; key: ProfileStrengthItemKey; section: ProfileStrengthSectionId };

export interface ProfileStrength {
  /** `complete ÷ total` items, rounded. Rendered verbatim. */
  percent: number;
  level: ProfileStrengthLevel;
  completeItems: number;
  totalItems: number;
  sections: ProfileStrengthSection[];
  readiness: {
    /** What the CheckUp needs before it can start. */
    start: ProfileReadinessMeter;
    /** What the iQulture score reads — every `score` item, start ones included. */
    score: ProfileReadinessMeter;
  };
  /** Null once everything is complete. */
  nextStep: ProfileNextStep | null;
}
